import { FC, useEffect } from 'react';
import * as slider from '@zag-js/slider';
import { useMachine, normalizeProps } from '@zag-js/react';
import { useUniqueId } from 'app/hooks/useUniqueId';
import { isUndefined } from '@silo/utils';
import {
  POINTS_LARGE_ICON_WIDTH,
  POINTS_SMALL_ICON_WIDTH,
  PositionedPoints,
  RangePoint,
  useGrabbingCursor,
  useSyncValues,
} from '@silo/ui';

interface DualVariantSliderProps {
  currentRiskFactorPoints: number;
  riskFactorPoints: number;
  setRiskFactorPoints: (value: number) => void;
  max?: number;
  min?: number;
  disabled?: boolean;
}

export const DualVariantSlider: FC<DualVariantSliderProps> = ({
  currentRiskFactorPoints,
  riskFactorPoints,
  setRiskFactorPoints,
  max,
  min,
  disabled = false,
}) => {
  const id = useUniqueId();
  const [state, send] = useMachine(slider.machine({ id: `existing-loan-slider-${id}` }), { context: { disabled } });
  const api = slider.connect(state, send, normalizeProps);

  useSyncValues({
    parentValue: riskFactorPoints,
    setParentValue: setRiskFactorPoints,
    transformParentValue: Math.round,
    localValue: api.value,
    setLocalValue: api.setValue,
    transformLocalValue: Math.round,
  });

  useEffect(() => {
    if (isUndefined(max)) return;

    if (api.value > max) {
      api.setValue(max);
    }
  }, [max, api]);

  useEffect(() => {
    if (isUndefined(min)) return;

    if (api.value < min) {
      api.setValue(min);
    }
  }, [min, api]);

  useGrabbingCursor(api.isDragging);

  return (
    <>
      <PositionedPoints
        points={[currentRiskFactorPoints]}
        indicators={[
          <RangePoint
            direction="down"
            label={<span className="text-sm text-shale-200">Current</span>}
            iconClassName="text-shale-200"
            size={POINTS_SMALL_ICON_WIDTH}
          />,
        ]}
      />

      <div className="pt-1 pb-10" {...api.rootProps}>
        <div {...api.controlProps}>
          <div className="h-2.5 health-gradient-bg rounded-full" {...api.trackProps} />
          <div className={`group mt-1 ${api.isDragging ? 'cursor-grabbing' : 'cursor-grab'}`} {...api.thumbProps}>
            <input {...api.hiddenInputProps} />

            <PositionedPoints
              points={[api.value]}
              indicators={[
                <RangePoint
                  direction="up"
                  size={POINTS_LARGE_ICON_WIDTH}
                  label={<span className="text-sm text-shale-200">New</span>}
                  iconClassName="relative text-slate-100 transition-[filter,background-color] group-hover:text-white group-active:text-white group-focus:text-white group-hover:ease-in group-active:ease-in group-focus:ease-in duration-100 group-hover:drop-shadow-slider group-active:drop-shadow-slider group-focus:drop-shadow-slider"
                />,
              ]}
            />
          </div>
        </div>
      </div>
    </>
  );
};
